// Original score: “The shop after closing”. A recurring melody in D Lydian
// over a steady D–A foundation; the raised fourth is a brief, quiet colour.
// These are musical gestures, never a second channel of grading information.
export const AUDIO_STORAGE = 'dream-pedlary-audio-v1';
const DEFAULTS = { music: 28, effects: 55, muted: false };
const PHRASES = [
  [[1, 66], [3.5, 69], [6, 71], [8.5, 69]],
  [[1, 66], [3.5, 64], [6, 66], [8.5, 62]],
  [[1, 66], [3.5, 68], [5.5, 69], [8.5, 66]],
  [[1, 64], [3.5, 66], [6, 64], [8.5, 62]],
  [[1, 66], [3.5, 69], [6, 71], [8.5, 69]],
  [[1, 66], [3.5, 64], [6, 66], [8.5, 64]],
  [[1, 66], [3.5, 68], [5.5, 69], [8.5, 64]],
  [[1, 64], [3.5, 66], [6, 64], [8.5, 62]],
];
const LOOP_SECONDS = PHRASES.length * 12;
const SCORE = PHRASES.flatMap((notes, phrase) => [
  { at: phrase * 12, pitch: 50, length: 14, level: .055, voice: 'soft' },
  { at: phrase * 12 + .6, pitch: 57, length: 14, level: .025, voice: 'soft' },
  ...notes.map(([at, pitch], i) => ({ at: phrase * 12 + at, pitch,
    length: pitch === 68 ? 1.6 : 2.8, level: pitch === 68 ? .075 : .13,
    voice: i === 2 && phrase % 4 === 0 ? 'glass' : 'piano' })),
]).sort((a, b) => a.at - b.at);

export function restoreAudioSettings(raw) {
  const level = (key) => typeof raw?.[key] === 'number' && Number.isFinite(raw[key])
    ? Math.max(0, Math.min(100, raw[key])) : DEFAULTS[key];
  return { music: level('music'), effects: level('effects'), muted: typeof raw?.muted === 'boolean' ? raw.muted : false };
}

export function createSoundscape({ storage, AudioContextClass = globalThis.AudioContext ?? globalThis.webkitAudioContext,
  setIntervalFn = globalThis.setInterval, clearIntervalFn = globalThis.clearInterval, onChange = () => {} } = {}) {
  let settings;
  try { storage ??= globalThis.localStorage; settings = restoreAudioSettings(JSON.parse(storage?.getItem(AUDIO_STORAGE) ?? 'null')); }
  catch { settings = { ...DEFAULTS }; }
  let context, musicBus, effectsBus, noiseBuffer, activated = false, hidden = false, disposed = false, failed = false;
  let scheduler, cursor = 0, origin = 0, rehearsing = false, effectsEpoch = 0, lastEffect = -Infinity;
  const voices = new Set();
  const canRun = () => activated && !hidden && !disposed && !failed && !settings.muted && (settings.music > 0 || settings.effects > 0);
  const emit = () => onChange();

  function stopVoices(channel) {
    for (const voice of voices) if (!channel || voice.channel === channel) voice.stop();
  }
  function stopMusic() {
    if (scheduler !== undefined) clearIntervalFn(scheduler);
    scheduler = undefined;
    stopVoices('music');
  }
  function cancelEffects() { effectsEpoch++; stopVoices('effects'); }
  function track(source, nodes, channel, end) {
    const voice = { channel, stop: () => { try { source.stop(); } catch { /* Already ended. */ } } };
    voices.add(voice);
    source.onended = () => { voices.delete(voice); for (const node of nodes) node.disconnect(); };
    source.stop(end);
  }
  function tone(pitch, at, length, level, voice = 'piano', channel = 'effects') {
    const partials = voice === 'glass' ? [[1, 1], [2.003, .22], [4.01, .065]]
      : voice === 'soft' ? [[1, 1], [2, .1]] : [[1, 1], [2, .27], [3, .07]];
    for (const [ratio, strength] of partials) {
      const oscillator = context.createOscillator(), envelope = context.createGain();
      oscillator.frequency.value = 440 * 2 ** ((pitch - 69) / 12) * ratio;
      const foundation = voice === 'soft' && channel === 'music';
      const attack = foundation ? 1.8 : voice === 'soft' ? .28 : .018;
      envelope.gain.setValueAtTime(0, at);
      envelope.gain.linearRampToValueAtTime(level * strength, at + attack);
      if (foundation) envelope.gain.setValueAtTime(level * strength, at + length - 4);
      envelope.gain.exponentialRampToValueAtTime(.0001, at + length);
      envelope.gain.linearRampToValueAtTime(0, at + length + .08);
      oscillator.connect(envelope); envelope.connect(channel === 'music' ? musicBus : effectsBus);
      oscillator.start(at); track(oscillator, [oscillator, envelope], channel, at + length + .1);
    }
  }
  function hush(at, length, level, frequency, type = 'lowpass') {
    const source = context.createBufferSource(), filter = context.createBiquadFilter(), envelope = context.createGain();
    source.buffer = noiseBuffer; source.loop = true;
    filter.type = type; filter.frequency.value = frequency; filter.Q.value = .5;
    envelope.gain.setValueAtTime(0, at);
    envelope.gain.linearRampToValueAtTime(level, at + Math.min(.3, length * .25));
    envelope.gain.exponentialRampToValueAtTime(.0001, at + length);
    envelope.gain.linearRampToValueAtTime(0, at + length + .03);
    source.connect(filter); filter.connect(envelope); envelope.connect(effectsBus);
    source.start(at); track(source, [source, filter, envelope], 'effects', at + length + .05);
  }
  function initialise() {
    context = new AudioContextClass();
    musicBus = context.createGain(); effectsBus = context.createGain();
    musicBus.gain.value = 0; effectsBus.gain.value = 0;
    const master = context.createGain(); master.gain.value = .7; master.connect(context.destination);
    musicBus.connect(master); effectsBus.connect(master);
    // A short, quiet room, with a deterministic impulse; no downloads or assets.
    let seed = 71203;
    const random = () => { seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0; return seed / 2147483648 - 1; };
    noiseBuffer = context.createBuffer(1, context.sampleRate, context.sampleRate);
    const noise = noiseBuffer.getChannelData(0);
    for (let i = 0; i < noise.length; i++) noise[i] = random();
    const room = context.createConvolver(), wet = context.createGain();
    const impulse = context.createBuffer(2, context.sampleRate * 2, context.sampleRate);
    for (let c = 0; c < 2; c++) {
      const data = impulse.getChannelData(c);
      for (let i = 0; i < data.length; i++) data[i] = random() * (1 - i / data.length) ** 3;
    }
    room.buffer = impulse; wet.gain.value = .16;
    musicBus.connect(room); effectsBus.connect(room); room.connect(wet); wet.connect(master);
    context.onstatechange = emit;
  }
  function levels() {
    if (!context) return;
    musicBus.gain.setTargetAtTime(settings.music / 100 * (rehearsing ? .45 : 1), context.currentTime, .18);
    effectsBus.gain.setTargetAtTime(settings.effects / 100, context.currentTime, .025);
  }
  function schedule() {
    if (!canRun() || !settings.music || context.state !== 'running') return;
    // If a foreground timer was delayed, leave a breath instead of playing a backlog.
    if (origin + SCORE[cursor].at < context.currentTime - .2) {
      cursor = 0; origin = context.currentTime + .15;
    }
    while (origin + SCORE[cursor].at < context.currentTime + .7) {
      const note = SCORE[cursor];
      tone(note.pitch, origin + note.at, note.length, note.level, note.voice, 'music');
      cursor++;
      if (cursor === SCORE.length) { cursor = 0; origin += LOOP_SECONDS; }
    }
  }
  async function sync() {
    if (!AudioContextClass || failed || disposed) return;
    if (!canRun()) {
      stopMusic(); cancelEffects();
      if (context && context.state !== 'closed') {
        try { await context.suspend(); } catch { /* Audio remains optional. */ }
      }
      emit(); return;
    }
    try {
      if (!context) initialise();
      await context.resume();
      // A hide/mute may have arrived while resume was pending.
      if (!canRun()) { await context.suspend(); return; }
      levels();
      if (!settings.effects) cancelEffects();
      if (!settings.music) stopMusic();
      else if (scheduler === undefined) {
        cursor = 0; origin = context.currentTime + .12;
        schedule(); scheduler = setIntervalFn(schedule, 250);
      }
      emit();
    } catch {
      failed = true; stopMusic(); cancelEffects();
      try { await context?.close(); } catch { /* Failed browser audio must not affect play. */ }
      emit();
    }
  }
  function effect(kind, item, stars = 1) {
    if (!canRun() || !settings.effects) return;
    const epoch = effectsEpoch;
    void sync().then(() => {
      if (epoch !== effectsEpoch || !canRun() || !settings.effects || context?.state !== 'running') return;
      const at = context.currentTime + .015;
      if (at - lastEffect < .045) return;
      lastEffect = at;
      if (kind === 'delivery') {
        cancelEffects();
        hush(at, .65, .08, 1600, 'bandpass');
        // Every successful dream gets the complete cadence.
        [62, 66, 69, 74].forEach((pitch, i) => tone(pitch, at + .12 + i * .28, 2.2, .14));
        if (stars >= 2) tone(78, at + 1.35, 2.1, .065, 'glass');
        if (stars >= 3) tone(81, at + 1.75, 1.9, .05, 'glass');
      } else if (kind === 'remove' || kind === 'clear') hush(at, .28, .12, 1200, 'bandpass');
      else if (kind === 'rule') { tone(76, at, .9, .055, 'glass'); tone(80, at + .16, 1.2, .035, 'glass'); }
      else if (kind === 'place' || kind === 'move') {
        if (['blanket', 'cloud', 'reeds', 'kite'].includes(item)) hush(at, .2, .12, 950, 'bandpass');
        else if (['lantern', 'sea'].includes(item)) tone(81, at, .55, .075, 'glass');
        else { tone(45, at, .12, .14, 'soft'); hush(at, .09, .09, 600); }
      } else if (kind === 'encounter') {
        if (item === 'sea') hush(at, 1.9, .19, 600);
        else if (item === 'reeds' || item === 'tree') hush(at, .85, .09, 1300, 'bandpass');
        else if (item === 'fireplace') hush(at, 1.1, .2, 380);
        else if (item === 'lantern') { tone(81, at, 1.1, .065, 'glass'); tone(78, at + .21, 1.1, .035, 'glass'); }
        else if (['cloud', 'kite'].includes(item)) { hush(at, .85, .075, 900, 'bandpass'); tone(76, at + .1, 1.2, .04, 'soft'); }
        else if (['gate', 'door', 'bench'].includes(item)) { tone(50, at, .22, .11, 'soft'); hush(at, .15, .08, 650); }
        else if (['blanket', 'dog'].includes(item)) hush(at, .4, .11, 650, 'bandpass');
      }
    });
  }
  return {
    get settings() { return { ...settings }; },
    get available() { return !!AudioContextClass && !failed; },
    get active() { return canRun() && context?.state === 'running'; },
    unlock() { if (disposed) return Promise.resolve(); activated = true; return sync(); },
    update(patch) {
      settings = restoreAudioSettings({ ...settings, ...patch });
      try { storage?.setItem(AUDIO_STORAGE, JSON.stringify(settings)); } catch { /* Session controls still work. */ }
      if (!settings.effects || settings.muted) cancelEffects();
      void sync(); emit();
    },
    setHidden(value) { hidden = value; return sync(); },
    setRehearsing(value) { if (rehearsing !== value) { rehearsing = value; levels(); } },
    cancelEffects,
    effect,
    async dispose() {
      disposed = true; stopMusic(); cancelEffects();
      if (context) { context.onstatechange = null; await context.close(); }
    },
  };
}
